<template>
  <NuxtLayout>
    <component :is="page.video_url ? videoHeader : heroHeader" class="offersHeader" :class="{'offersHeader--Expiration': page.expiration}" :title-visible="false" stacked-elements>
      <div class="heroHeader__StackedElementsContent container">
        <div class="offersHeader__Texts">
          <h1 v-text="page.title" />
  
          <div class="offersHeader__Accommodation">
            <p v-text="page.subtitle" />
          </div>
        </div>

        <ClientOnly v-if="page.expiration">
          <div class="offersHeader__Sticker" :class="{'offersHeader__Sticker--Narrow': daysLeft && daysLeft > 0, 'offersHeader__Sticker--Expired': timeOfferExpired}" v-if="page.expiration">
            <p v-if="!timeOfferExpired">
              <span v-if="page.text_above_counter">{{ page.text_above_counter }}</span>
              <strong>{{ timerValue }}</strong>
              <span v-if="daysLeft && daysLeft > 0">{{ $t('offerGroupDetail.days', daysLeft) }}</span>
            </p>
            <p v-else>
              <span>{{ $t('offerGroupDetail.offerExpired') }}</span>
            </p>
          </div>
        </ClientOnly>
        <div v-else-if="page.image_document_discount?.meta?.format === 'svg' && page.image_document_discount.meta.url" class="offersHeader__DiscountWrapper">
          <img :src="page.image_document_discount.meta.url" :alt="page.image_document_discount.title || ''" width="300" height="200" />
        </div>
      </div>
    </component>

    <transition name="fade">
      <OfferExpiredRibbon v-if="offerExpired" />
    </transition>

    <div class="offers__Description container" v-if="page.description || page.newsletter_text">
      <div class="richText" v-html="page.description" v-if="page.description"/>
      <div class="offers__DescriptionNewsletter" v-if="page.newsletter_text">
        <p v-text="page.newsletter_text" />
        <TextField v-model="newsletterEmail" :placeholder="$t('newsletter.placeholder')">
          <template #append>
            <button type="button" @click="submitNewsletter">
              {{ $t('newsletter.submit') }}
              <Icon name="arrow-right-long" />
            </button>
          </template>
        </TextField>
      </div>
    </div>
  
    <Pagination
      ref="paginationElement"
      endpoint="/offer/"
      :customApiOptions="{ [FilterIdentifiersObject.OFFER_GROUP.backendId]: props.page.id }"
      :selectedFilters="selectedFilters"
      @search="data = $event"
      class="offers__ListWrapper"
      counterText="offersAvailable"
    >
			<template #filters-top>
				<Filters :data="filtersList" v-model="selectedFilters" @close="filterData" @item-remove="filterData" />
			</template>

      <OfferItem v-for="offer in data.results" :data="offer" :key="offer.id" />

      <template #no-results>
				<p v-text="$t('noResultsOffers')" />
			</template>
		</Pagination>
  </NuxtLayout>
</template>

<script setup>
  defineOptions({
    auth: 'aminess'
  });
  
  const props = defineProps({
    page: {
      type: Object
    }
  })

  useHead({
    htmlAttrs: {
      class: 'fixedHeader fixedHeader--WithoutTopPadding',
    },
  })

  const { t } = useI18n();
  const { initialData, link } = useSetup();
  const { query } = useRoute();
  const selectedFilters = ref({});
  const offerExpired = ref(!!query.offer_expired);
  const data = ref([]);
  const paginationElement = ref(null);
  const heroHeader = resolveComponent('HeroHeader');
  const videoHeader = resolveComponent('VideoHeader');
  const newsletterEmail = ref('');
  const today = ref(Date.now());
  const timeOfferExpired = ref(false);
  const timerValue = ref('0');
  const SECOND_IN_MILLISECONDS = 1000;
  const MINUTE_IN_MILLISECONDS = SECOND_IN_MILLISECONDS * 60;
  const HOUR_IN_MILLISECONDS = MINUTE_IN_MILLISECONDS * 60;
  const DAY_IN_MILLISECONDS = HOUR_IN_MILLISECONDS * 24;

  const daysLeft = ref(0);
  let countdownTo = new Date(props.page.expiration).getTime();

  const queryParams = computed(() => {
    const _query = JSON.parse(JSON.stringify(query));
    for (const key of ['dates', 'date_from', 'date_to']) delete _query[key]
    return _query;
  });

  const initialResponseResultsLength = data.value?.results?.length || 0;

  const filtersList = [
    {
      title: t('filtersTexts.region'),
      identifier: FilterIdentifiersObject.REGION.backendId,
      items: initialData.value[FilterIdentifiersObject.REGION.frontendId] ? initialData.value[FilterIdentifiersObject.REGION.frontendId] : []
    },
    {
      title: t('filtersTexts.propertyType'),
      identifier: FilterIdentifiersObject.PROPERTY_TYPE.backendId,
      items: getPropertyTypeFilters(t)
    },
    {
      title: t('filtersTexts.holidayType'),
      identifier: FilterIdentifiersObject.HOLIDAY_TYPE.backendId,
      items: initialData.value.amenities?.[FilterIdentifiersObject.HOLIDAY_TYPE.frontendId] ? initialData.value.amenities[FilterIdentifiersObject.HOLIDAY_TYPE.frontendId] : []
    },
  ];

  async function filterData() {
		paginationElement.value.filterData();
  }

  function submitNewsletter() {
    if (newsletterEmail.value.length > 0) {
      navigateTo({ path: link('Newsletter'), query: { email: newsletterEmail.value, } });
    } else navigateTo({ path: link('Newsletter') });
  }

  function countdown() {
    const currentTime = performance.now();
    const timeLeft = countdownTo - today.value - currentTime;

    if (timeLeft < 0) {
      timeOfferExpired.value = true;
      return;
    }

    daysLeft.value = Math.floor(timeLeft / DAY_IN_MILLISECONDS);
    const hoursLeft = Math.floor(
      (timeLeft % DAY_IN_MILLISECONDS) / HOUR_IN_MILLISECONDS
    ).toLocaleString('en-US', { minimumIntegerDigits: 2 });
    const minutesLeft = Math.floor(
      (timeLeft % HOUR_IN_MILLISECONDS) / MINUTE_IN_MILLISECONDS
    ).toLocaleString('en-US', { minimumIntegerDigits: 2 });
    const secondsLeft = Math.floor(
      (timeLeft % MINUTE_IN_MILLISECONDS) / SECOND_IN_MILLISECONDS
    ).toLocaleString('en-US', { minimumIntegerDigits: 2 });

    if(daysLeft.value && daysLeft.value > 0) {
      timerValue.value = `${daysLeft.value}`;
    }
    else {
      timerValue.value = `${hoursLeft}:${minutesLeft}:${secondsLeft}`;
    }

    requestAnimationFrame(countdown);
  }

  onMounted(() => {
    if (offerExpired.value) {
      setTimeout(() => {
        offerExpired.value = false;
        const { offer_expired, ...restQuery } = query;
        navigateTo({ query: restQuery }, { replace: true });
      }, 10000);
    }

    requestAnimationFrame(countdown);
  });
</script>

<style lang="scss">
  @import url('@/assets/styles/pages/_offers.scss');
  @import url('@/assets/styles/components/blocks/_richText.scss');
</style>